// Typed data-access layer: one small block of methods per resource
// (list/create/update/delete/subscribe), all delegating to apiClient. The
// route paths here must match the registry entries in
// backend/internal/resources/registry.go. To add a resource, copy an
// existing block — see the recipes in docs/ARCHITECTURE.md.

import type {
  FirestoreAccount,
  FirestoreCategory,
  FirestoreDebt,
  FirestoreDocument,
  FirestoreExpense,
  FirestoreExpenseComment,
  FirestoreIncome,
  FirestoreInvestment,
  FirestoreSavingsGoal,
  FirestoreSavingsTransaction,
  FirestoreSettlement,
  FirestoreShoppingList,
  FirestoreShoppingListItem,
  FirestoreUserPreferences,
} from "../types/firestore"
import type { FirestoreGroceryPrice, FirestoreGroceryProduct } from "../types/grocery"
import type { NetWorthSnapshot, ServerExportBundle } from "../types/netWorth"
import type { FeedbackContext } from "../utils/feedbackContext"
import { apiRequest, subscribeViaApi, type Unsubscribe } from "./apiClient"
import type { AuthUser } from "./authService"

export type { Unsubscribe }

// A document as accepted for creation: caller supplies everything except the
// envelope the service stamps on (id/userId/createdAt/updatedAt).
type NewDoc<T extends FirestoreDocument> = Omit<T, "id" | "userId" | "createdAt" | "updatedAt">
// Fields a caller may patch: anything but the immutable envelope identity.
// PATCH semantics: `undefined` keys are dropped by JSON.stringify and the
// server treats missing keys as "unchanged" — so CLEARING a field requires an
// explicit `null` (bodyToColumns passes null through to set the column NULL).
export type DocUpdate<T extends FirestoreDocument> = {
  [K in keyof Omit<T, "id" | "userId" | "createdAt">]?: Omit<T, "id" | "userId" | "createdAt">[K] | null
}

class DatabaseService {
  private userId: string | null = null
  private householdId: string | null = null
  private isHouseholdContext: boolean = false

  setUser(user: AuthUser | null) {
    this.userId = user?.uid || null
  }

  setHouseholdContext(householdId: string | null) {
    this.householdId = householdId
    this.isHouseholdContext = !!householdId
  }

  setPersonalContext() {
    this.householdId = null
    this.isHouseholdContext = false
  }

  // ---------------------------------------------------------------------------
  // REST API path. Reads/writes hit /api; live updates come from a Supabase
  // Realtime channel that just tells us to refetch (the API stays the single
  // source of truth).
  // ---------------------------------------------------------------------------
  private apiCreate(path: string, body: unknown): Promise<string> {
    return apiRequest<{ id: string }>(path, {
      method: "POST",
      body,
      householdId: this.householdId,
    }).then((r) => r.id)
  }

  private apiUpdate(path: string, body: unknown): Promise<void> {
    return apiRequest<void>(path, { method: "PATCH", body, householdId: this.householdId })
  }

  private apiDelete(path: string): Promise<void> {
    return apiRequest<void>(path, { method: "DELETE", householdId: this.householdId })
  }

  // Filter for the live channel: household rows scoped by household_id; personal
  // rows scoped by user_id.
  private realtimeFilter(): string | undefined {
    if (this.isHouseholdContext && this.householdId) return `household_id=eq.${this.householdId}`
    if (this.userId) return `user_id=eq.${this.userId}`
    return undefined
  }

  private subscribe<T>(table: string, path: string, callback: (rows: T[]) => void): Unsubscribe {
    return subscribeViaApi<T[]>(table, path, callback, {
      householdId: this.householdId,
      filter: this.realtimeFilter(),
    })
  }

  // EXPENSES
  createExpense(expense: NewDoc<FirestoreExpense>) {
    return this.apiCreate("/api/expenses", expense)
  }
  updateExpense(expenseId: string, updates: DocUpdate<FirestoreExpense>) {
    return this.apiUpdate(`/api/expenses/${expenseId}`, updates)
  }
  deleteExpense(expenseId: string) {
    return this.apiDelete(`/api/expenses/${expenseId}`)
  }
  subscribeToExpenses(callback: (expenses: FirestoreExpense[]) => void): Unsubscribe {
    return this.subscribe<FirestoreExpense>("expenses", "/api/expenses", callback)
  }

  // CATEGORIES
  // One-shot read (no live subscription). Used by the store's cold-start
  // "is this a new user?" check so a failing read surfaces as a rejected
  // promise instead of hanging a subscribe-once forever.
  getCategories(): Promise<FirestoreCategory[]> {
    return apiRequest<FirestoreCategory[]>("/api/categories", { householdId: this.householdId })
  }
  createCategory(category: NewDoc<FirestoreCategory>) {
    return this.apiCreate("/api/categories", category)
  }
  updateCategory(categoryId: string, updates: DocUpdate<FirestoreCategory>) {
    return this.apiUpdate(`/api/categories/${categoryId}`, updates)
  }
  deleteCategory(categoryId: string) {
    return this.apiDelete(`/api/categories/${categoryId}`)
  }
  subscribeToCategories(callback: (categories: FirestoreCategory[]) => void): Unsubscribe {
    return this.subscribe<FirestoreCategory>("categories", "/api/categories", callback)
  }

  // ACCOUNTS
  createAccount(account: NewDoc<FirestoreAccount>) {
    return this.apiCreate("/api/accounts", account)
  }
  updateAccount(accountId: string, updates: DocUpdate<FirestoreAccount>) {
    return this.apiUpdate(`/api/accounts/${accountId}`, updates)
  }
  deleteAccount(accountId: string) {
    return this.apiDelete(`/api/accounts/${accountId}`)
  }
  subscribeToAccounts(callback: (accounts: FirestoreAccount[]) => void): Unsubscribe {
    return this.subscribe<FirestoreAccount>("accounts", "/api/accounts", callback)
  }

  // INCOME
  createIncome(income: NewDoc<FirestoreIncome>) {
    return this.apiCreate("/api/income", income)
  }
  updateIncome(incomeId: string, updates: DocUpdate<FirestoreIncome>) {
    return this.apiUpdate(`/api/income/${incomeId}`, updates)
  }
  deleteIncome(incomeId: string) {
    return this.apiDelete(`/api/income/${incomeId}`)
  }
  subscribeToIncome(callback: (income: FirestoreIncome[]) => void): Unsubscribe {
    return this.subscribe<FirestoreIncome>("income", "/api/income", callback)
  }

  // SAVINGS GOALS
  createSavingsGoal(goal: NewDoc<FirestoreSavingsGoal>) {
    return this.apiCreate("/api/savings-goals", goal)
  }
  updateSavingsGoal(goalId: string, updates: DocUpdate<FirestoreSavingsGoal>) {
    return this.apiUpdate(`/api/savings-goals/${goalId}`, updates)
  }
  deleteSavingsGoal(goalId: string) {
    return this.apiDelete(`/api/savings-goals/${goalId}`)
  }
  subscribeToSavingsGoals(callback: (goals: FirestoreSavingsGoal[]) => void): Unsubscribe {
    return this.subscribe<FirestoreSavingsGoal>("savings_goals", "/api/savings-goals", callback)
  }

  // INVESTMENTS
  createInvestment(investment: NewDoc<FirestoreInvestment>) {
    return this.apiCreate("/api/investments", investment)
  }
  updateInvestment(investmentId: string, updates: DocUpdate<FirestoreInvestment>) {
    return this.apiUpdate(`/api/investments/${investmentId}`, updates)
  }
  deleteInvestment(investmentId: string) {
    return this.apiDelete(`/api/investments/${investmentId}`)
  }
  subscribeToInvestments(callback: (investments: FirestoreInvestment[]) => void): Unsubscribe {
    return this.subscribe<FirestoreInvestment>("investments", "/api/investments", callback)
  }

  // DEBTS (loans — the liability side)
  createDebt(debt: NewDoc<FirestoreDebt>) {
    return this.apiCreate("/api/debts", debt)
  }
  updateDebt(debtId: string, updates: DocUpdate<FirestoreDebt>) {
    return this.apiUpdate(`/api/debts/${debtId}`, updates)
  }
  deleteDebt(debtId: string) {
    return this.apiDelete(`/api/debts/${debtId}`)
  }
  subscribeToDebts(callback: (debts: FirestoreDebt[]) => void): Unsubscribe {
    return this.subscribe<FirestoreDebt>("debts", "/api/debts", callback)
  }

  // SAVINGS TRANSACTIONS
  createSavingsTransaction(transaction: NewDoc<FirestoreSavingsTransaction>) {
    return this.apiCreate("/api/savings-transactions", transaction)
  }
  updateSavingsTransaction(transactionId: string, updates: DocUpdate<FirestoreSavingsTransaction>) {
    return this.apiUpdate(`/api/savings-transactions/${transactionId}`, updates)
  }
  deleteSavingsTransaction(transactionId: string) {
    return this.apiDelete(`/api/savings-transactions/${transactionId}`)
  }
  subscribeToSavingsTransactions(callback: (transactions: FirestoreSavingsTransaction[]) => void): Unsubscribe {
    return this.subscribe<FirestoreSavingsTransaction>("savings_transactions", "/api/savings-transactions", callback)
  }

  // SETTLEMENTS (household repayments)
  createSettlement(settlement: NewDoc<FirestoreSettlement>) {
    return this.apiCreate("/api/settlements", settlement)
  }
  deleteSettlement(settlementId: string) {
    return this.apiDelete(`/api/settlements/${settlementId}`)
  }
  subscribeToSettlements(callback: (settlements: FirestoreSettlement[]) => void): Unsubscribe {
    return this.subscribe<FirestoreSettlement>("settlements", "/api/settlements", callback)
  }

  // EXPENSE COMMENTS (note thread on a transaction — create/delete only)
  createExpenseComment(comment: NewDoc<FirestoreExpenseComment>) {
    return this.apiCreate("/api/expense-comments", comment)
  }
  deleteExpenseComment(commentId: string) {
    return this.apiDelete(`/api/expense-comments/${commentId}`)
  }
  subscribeToExpenseComments(callback: (comments: FirestoreExpenseComment[]) => void): Unsubscribe {
    return this.subscribe<FirestoreExpenseComment>("expense_comments", "/api/expense-comments", callback)
  }

  // SHOPPING LISTS
  createShoppingList(list: NewDoc<FirestoreShoppingList>) {
    return this.apiCreate("/api/shopping-lists", list)
  }
  updateShoppingList(listId: string, updates: DocUpdate<FirestoreShoppingList>) {
    return this.apiUpdate(`/api/shopping-lists/${listId}`, updates)
  }
  deleteShoppingList(listId: string) {
    return this.apiDelete(`/api/shopping-lists/${listId}`)
  }
  subscribeToShoppingLists(callback: (lists: FirestoreShoppingList[]) => void): Unsubscribe {
    return this.subscribe<FirestoreShoppingList>("shopping_lists", "/api/shopping-lists", callback)
  }

  // SHOPPING LIST ITEMS
  createShoppingListItem(item: NewDoc<FirestoreShoppingListItem>) {
    return this.apiCreate("/api/shopping-list-items", item)
  }
  updateShoppingListItem(itemId: string, updates: DocUpdate<FirestoreShoppingListItem>) {
    return this.apiUpdate(`/api/shopping-list-items/${itemId}`, updates)
  }
  deleteShoppingListItem(itemId: string) {
    return this.apiDelete(`/api/shopping-list-items/${itemId}`)
  }
  subscribeToShoppingListItems(callback: (items: FirestoreShoppingListItem[]) => void): Unsubscribe {
    return this.subscribe<FirestoreShoppingListItem>("shopping_list_items", "/api/shopping-list-items", callback)
  }

  // GROCERY PRODUCTS
  createGroceryProduct(product: NewDoc<FirestoreGroceryProduct>) {
    return this.apiCreate("/api/grocery-products", product)
  }
  updateGroceryProduct(productId: string, updates: DocUpdate<FirestoreGroceryProduct>) {
    return this.apiUpdate(`/api/grocery-products/${productId}`, updates)
  }
  deleteGroceryProduct(productId: string) {
    return this.apiDelete(`/api/grocery-products/${productId}`)
  }
  subscribeToGroceryProducts(callback: (products: FirestoreGroceryProduct[]) => void): Unsubscribe {
    return this.subscribe<FirestoreGroceryProduct>("grocery_products", "/api/grocery-products", callback)
  }

  // GROCERY PRICES
  createGroceryPrice(price: NewDoc<FirestoreGroceryPrice>) {
    return this.apiCreate("/api/grocery-prices", price)
  }
  subscribeToGroceryPrices(callback: (prices: FirestoreGroceryPrice[]) => void): Unsubscribe {
    return this.subscribe<FirestoreGroceryPrice>("grocery_prices", "/api/grocery-prices", callback)
  }
  async batchCreateGroceryPrices(prices: NewDoc<FirestoreGroceryPrice>[]) {
    await apiRequest<unknown>("/api/grocery-prices/batch", {
      method: "POST",
      body: prices,
      householdId: this.householdId,
    })
  }

  // NET WORTH SNAPSHOTS (server-owned nightly history — read-only)
  /**
   * Net worth history for a context.
   *
   * Takes the household id explicitly because this is a one-shot fetch from a
   * component's onMounted, not a store subscription. `this.householdId` is set
   * during the context switch, which is not sequenced with an arbitrary
   * component mounting — a card that mounts first would fetch the personal
   * history and render it under a household heading. Callers pass the
   * reactive context and refetch when it changes.
   */
  getNetWorthSnapshots(householdId?: string | null): Promise<NetWorthSnapshot[]> {
    return apiRequest<NetWorthSnapshot[]>("/api/net-worth-snapshots", {
      householdId: householdId === undefined ? this.householdId : householdId,
    })
  }

  // SERVER-SIDE EXPORT — complete, authoritative copy of the active context
  // (every registry collection), independent of what's loaded client-side.
  exportAllData(): Promise<ServerExportBundle> {
    return apiRequest<ServerExportBundle>("/api/data/export", { householdId: this.householdId })
  }

  // FEEDBACK (write-only from the client: submit a message + context snapshot)
  submitFeedback(message: string, context: FeedbackContext) {
    return this.apiCreate("/api/feedback", { message, context })
  }

  // USER PREFERENCES (singleton per context — fetched as a single object)
  getUserPreferences(): Promise<FirestoreUserPreferences | null> {
    return apiRequest<FirestoreUserPreferences | null>("/api/preferences", {
      householdId: this.householdId,
    })
  }

  async updateUserPreferences(preferences: DocUpdate<FirestoreUserPreferences>) {
    await apiRequest<unknown>("/api/preferences", {
      method: "PUT",
      body: preferences,
      householdId: this.householdId,
    })
  }

  // UTILITY METHODS — kept as no-ops/passthroughs for backwards compatibility
  // with the localStorage migration path. The API has no concept of "migrating
  // localStorage" data; we'd just re-post each item.
  async migrateLocalData(localData: {
    expenses?: NewDoc<FirestoreExpense>[]
    categories?: NewDoc<FirestoreCategory>[]
    accounts?: NewDoc<FirestoreAccount>[]
  }) {
    for (const expense of localData.expenses ?? []) await this.createExpense(expense)
    for (const category of localData.categories ?? []) await this.createCategory(category)
    for (const account of localData.accounts ?? []) await this.createAccount(account)
  }

  async migrateInvestments(investmentsData: NewDoc<FirestoreInvestment>[]) {
    for (const investment of investmentsData) await this.createInvestment(investment)
  }

  async clearUserData() {
    await apiRequest<void>("/api/data", { method: "DELETE", householdId: this.householdId })
  }
}

// Export singleton instance
export const databaseService = new DatabaseService()
